import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'context/AuthContext';
import { IniciativaFormatada } from 'types/iniciativa';
import {
  AvaliacaoEtapa2,
  AvaliacaoEtapa2Input,
  AvaliacaoIniciativa,
  AvaliacoesMap,
  CriterioKey,
  NotasCriterios,
  calcularTotalNotas,
  isAvaliacaoCompleta,
} from 'types/etapa2';
import {
  fetchAvaliacaoAtivaEtapa2,
  invalidarCacheAvaliacoesEtapa2,
  submitAvaliacaoEtapa2,
} from 'services/avaliacao-etapa2.service';
import { buscarIniciativasEtapa2 } from 'services/iniciativa.service';
import {
  buscarRascunho,
  deletarRascunho,
  salvarRascunho,
} from 'services/rascunho-etapa2.service';

const RASCUNHO_PREFIX = 'votacao-etapa-2-rascunho';
const rascunhoKey = (userId: string, category: string) =>
  `${RASCUNHO_PREFIX}:${userId}:${category}`;

interface UseAvaliacaoEtapa2 {
  iniciativas: IniciativaFormatada[];
  loadingIniciativas: boolean;
  erroIniciativas: string | null;

  avaliacoes: AvaliacoesMap;
  setNota: (iniciativaId: string, criterio: CriterioKey, valor: number) => void;
  removerAvaliacao: (iniciativaId: string) => void;

  podeEnviar: boolean;
  totalAvaliadas: number;
  totalIniciativas: number;

  enviando: boolean;
  enviar: () => Promise<void>;
  erroEnvio: string | null;

  avaliacaoAtiva: AvaliacaoEtapa2 | null;
  isReadOnly: boolean;
  editMode: boolean;
  iniciarEdicao: () => void;
  cancelarEdicao: () => void;
}

export function useAvaliacaoEtapa2(category: string | null): UseAvaliacaoEtapa2 {
  const { user } = useAuth();
  const [iniciativas, setIniciativas] = useState<IniciativaFormatada[]>([]);
  const [loadingIniciativas, setLoadingIniciativas] = useState(false);
  const [erroIniciativas, setErroIniciativas] = useState<string | null>(null);
  const [avaliacoes, setAvaliacoes] = useState<AvaliacoesMap>({});
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [avaliacaoAtiva, setAvaliacaoAtiva] = useState<AvaliacaoEtapa2 | null>(null);
  const [editMode, setEditMode] = useState(false);

  // Refs para leitura em callbacks sem adicionar às dependências do useCallback
  const isReadOnlyRef = useRef(false);
  const avaliacaoAtivaRef = useRef<AvaliacaoEtapa2 | null>(null);
  // Sinaliza que a carga inicial (remoto/rascunho) já completou para a
  // combinação atual de category/userId. Enquanto false, o effect que
  // persiste rascunho não escreve (evita wipe do localStorage no mount com
  // avaliacoes={} inicial).
  const loadDoneRef = useRef(false);

  // Ref-based copy de avaliacoes para evitar staleness em callbacks assíncronos
  const avaliacoesRef = useRef<AvaliacoesMap>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasUnsyncedChangesRef = useRef(false);

  // Carrega iniciativas + hidrata avaliação ativa remota (tem prioridade) ou rascunho local
  useEffect(() => {
    loadDoneRef.current = false;

    if (!category) {
      setIniciativas([]);
      setAvaliacoes({});
      setAvaliacaoAtiva(null);
      avaliacaoAtivaRef.current = null;
      setEditMode(false);
      isReadOnlyRef.current = false;
      return;
    }

    let canceled = false;

    const load = async () => {
      setLoadingIniciativas(true);
      setErroIniciativas(null);
      try {
        const [inis, ativa, rascunhoRemoto] = await Promise.all([
          buscarIniciativasEtapa2(category, user?._id),
          user?._id
            ? fetchAvaliacaoAtivaEtapa2(user._id, category)
            : Promise.resolve(null),
          user?._id
            ? buscarRascunho(user._id, category)
            : Promise.resolve(null),
        ]);
        if (canceled) return;

        setIniciativas(inis);
        setEditMode(false);

        if (ativa) {
          // Avaliação remota ativa: hidratar com notas remotas, limpar rascunho local e remoto
          setAvaliacaoAtiva(ativa);
          avaliacaoAtivaRef.current = ativa;
          isReadOnlyRef.current = true;

          const inicial: AvaliacoesMap = {};
          ativa.avaliacoes.forEach((av: AvaliacaoIniciativa) => {
            inicial[av.iniciativaId] = av.notas;
          });
          setAvaliacoes(inicial);

          if (user?._id) {
            // Limpar rascunho local residual
            try {
              window.localStorage.removeItem(rascunhoKey(user._id, category));
            } catch {
              /* ignora */
            }
            // Limpar rascunho remoto residual
            deletarRascunho(user._id, category).catch(console.error);
          }
        } else if (rascunhoRemoto) {
          // Sem avaliação enviada, mas há rascunho remoto: usá-lo como estado inicial
          setAvaliacaoAtiva(null);
          avaliacaoAtivaRef.current = null;
          isReadOnlyRef.current = false;

          setAvaliacoes(rascunhoRemoto);

          // Espelhar no localStorage para que o modo offline também funcione
          if (user?._id) {
            try {
              window.localStorage.setItem(
                rascunhoKey(user._id, category),
                JSON.stringify(rascunhoRemoto)
              );
            } catch {
              /* ignora */
            }
          }
        } else {
          // Sem avaliação enviada nem rascunho remoto: carregar rascunho local se houver
          setAvaliacaoAtiva(null);
          avaliacaoAtivaRef.current = null;
          isReadOnlyRef.current = false;

          let inicial: AvaliacoesMap = {};
          if (user?._id) {
            try {
              const raw = window.localStorage.getItem(rascunhoKey(user._id, category));
              if (raw) {
                inicial = JSON.parse(raw) as AvaliacoesMap;
              }
            } catch {
              // ignora
            }
          }
          setAvaliacoes(inicial);
        }

      } catch (err: unknown) {
        if (canceled) return;
        setErroIniciativas(
          err instanceof Error ? err.message : 'Erro ao carregar iniciativas'
        );
        setIniciativas([]);
      } finally {
        if (!canceled) {
          setLoadingIniciativas(false);
          loadDoneRef.current = true;
        }
      }
    };

    load();
    return () => {
      canceled = true;
    };
  }, [category, user?._id]);

  // isReadOnly derivado do estado atual
  const isReadOnly = !!avaliacaoAtiva && !editMode;

  // Mantém refs sincronizadas para uso nos callbacks
  isReadOnlyRef.current = isReadOnly;
  avaliacaoAtivaRef.current = avaliacaoAtiva;

  // Persiste rascunho no localStorage somente quando não estiver em modo read-only.
  // O guard de loadDoneRef evita que o estado inicial vazio sobrescreva o
  // rascunho ANTES da carga assíncrona do localStorage completar.
  // Também agenda um save remoto com debounce de 4 s.
  useEffect(() => {
    if (!category) return;
    if (!user?._id) return;
    if (isReadOnly) return;
    if (!loadDoneRef.current) return;

    // Atualiza ref para que o callback do debounce leia o valor mais recente
    avaliacoesRef.current = avaliacoes;
    hasUnsyncedChangesRef.current = true;

    try {
      window.localStorage.setItem(
        rascunhoKey(user._id, category),
        JSON.stringify(avaliacoes)
      );
    } catch {
      // ignora quota / disabled
    }

    // Debounce 4 s: cancela o timer anterior e agenda um novo
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      salvarRascunho(user._id, category, avaliacoesRef.current).catch(console.error);
      hasUnsyncedChangesRef.current = false;
    }, 4000);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [avaliacoes, category, isReadOnly, user?._id]);

  // Safety-net: persiste remotamente a cada 30 s se houver mudanças não sincronizadas
  useEffect(() => {
    if (!category || !user?._id) return;

    intervalRef.current = setInterval(() => {
      if (hasUnsyncedChangesRef.current && loadDoneRef.current) {
        salvarRascunho(user._id, category, avaliacoesRef.current).catch(console.error);
        hasUnsyncedChangesRef.current = false;
      }
    }, 30_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [category, user?._id]);

  // Flush antes do usuário sair da página (best-effort: XHR pode ser abortado
  // pelo browser; debounce e interval são os mecanismos primários confiáveis)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasUnsyncedChangesRef.current && category && user?._id) {
        salvarRascunho(user._id, category, avaliacoesRef.current).catch(console.error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [category, user?._id]);

  const setNota = useCallback(
    (iniciativaId: string, criterio: CriterioKey, valor: number) => {
      // Defesa em profundidade: no-op se read-only
      if (isReadOnlyRef.current) return;
      setAvaliacoes((prev) => ({
        ...prev,
        [iniciativaId]: {
          ...(prev[iniciativaId] ?? {}),
          [criterio]: valor,
        },
      }));
    },
    []
  );

  const removerAvaliacao = useCallback(
    (iniciativaId: string) => {
      // Defesa em profundidade: no-op se read-only
      if (isReadOnlyRef.current) return;
      setAvaliacoes((prev) => {
        const next = { ...prev };
        delete next[iniciativaId];
        return next;
      });
    },
    []
  );

  const iniciarEdicao = useCallback(() => {
    setEditMode(true);
    isReadOnlyRef.current = false;
  }, []);

  const cancelarEdicao = useCallback(() => {
    if (isReadOnlyRef.current) return;

    // Cancela o debounce pendente antes de restaurar o estado
    if (debounceRef.current) clearTimeout(debounceRef.current);

    setEditMode(false);
    isReadOnlyRef.current = true;
    hasUnsyncedChangesRef.current = false;
    const currentAtiva = avaliacaoAtivaRef.current;
    if (currentAtiva) {
      const inicial: AvaliacoesMap = {};
      currentAtiva.avaliacoes.forEach((av) => {
        inicial[av.iniciativaId] = av.notas;
      });
      setAvaliacoes(inicial);
    }
    if (category && user?._id) {
      try {
        window.localStorage.removeItem(rascunhoKey(user._id, category));
      } catch {
        /* ignora */
      }
      deletarRascunho(user._id, category).catch(console.error);
    }
  }, [category, user?._id]);

  const totalIniciativas = iniciativas.length;
  const totalAvaliadas = useMemo(
    () =>
      iniciativas.filter((ini) => isAvaliacaoCompleta(avaliacoes[ini.id]))
        .length,
    [iniciativas, avaliacoes]
  );
  const podeEnviar =
    totalIniciativas > 0 && totalAvaliadas === totalIniciativas;

  const enviar = useCallback(async () => {
    if (enviando) return;
    if (!user || !category || !podeEnviar) return;
    setEnviando(true);
    setErroEnvio(null);
    try {
      const avaliacoesPayload: AvaliacaoIniciativa[] = iniciativas.map((ini) => {
        const notas = avaliacoes[ini.id] as NotasCriterios;
        return {
          iniciativaId: String(ini.id),
          iniciativaTitulo: String(ini.titulo ?? ''),
          notas: { ...notas },
          total: calcularTotalNotas(notas),
        };
      });

      const matricula = String(
        user.detail?.acessor?.vp?.matricula ?? user.detail?.matricula ?? ''
      );

      const payload: AvaliacaoEtapa2Input = {
        category,
        avaliadorId: user._id,
        avaliadorName: String(user.name ?? ''),
        avaliadorMatricula: matricula,
        enviadoEm: Date.now(),
        avaliacoes: avaliacoesPayload,
        totalIniciativas: avaliacoesPayload.length,
      };

      const novaAvaliacao = await submitAvaliacaoEtapa2(payload);

      try {
        window.localStorage.removeItem(rascunhoKey(user._id, category));
      } catch {
        /* ignora */
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      hasUnsyncedChangesRef.current = false;
      deletarRascunho(user._id, category).catch(console.error);

      invalidarCacheAvaliacoesEtapa2(user._id, category);

      // Atualiza avaliacaoAtiva com o novo doc e sai do modo edit
      setAvaliacaoAtiva(novaAvaliacao);
      avaliacaoAtivaRef.current = novaAvaliacao;
      setEditMode(false);
      isReadOnlyRef.current = true;
    } catch (err: unknown) {
      const mensagem = err instanceof Error ? err.message : 'Erro ao enviar avaliação';
      setErroEnvio(mensagem);
      throw err;
    } finally {
      setEnviando(false);
    }
  }, [enviando, user, category, podeEnviar, iniciativas, avaliacoes]);

  return {
    iniciativas,
    loadingIniciativas,
    erroIniciativas,
    avaliacoes,
    setNota,
    removerAvaliacao,
    podeEnviar,
    totalAvaliadas,
    totalIniciativas,
    enviando,
    enviar,
    erroEnvio,
    avaliacaoAtiva,
    isReadOnly,
    editMode,
    iniciarEdicao,
    cancelarEdicao,
  };
}
